import type { Block } from '@vedalib/editor'
import type { LabeledGraphicElementValue, LabeledGraphicPoint } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { FileUsageSource, FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useMemo } from 'react'

import PanelIconImage from 'components/Editor/FloatingPanel/PanelIconImage'
import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { chainName, FieldColorPicker, FieldFile, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { IconButton, TileButton } from 'components/uiKit/Button'
import Divider from 'components/uiKit/Divider'
import { KitSize } from 'components/uiKit/KitTypes'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { useElementStyleCss, useElementStyleValues } from 'services/Branding/hooks'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getDeviceMode } from 'services/Store/Project/selectors'
import { t } from 'services/Translation'
import { FileTypeEnum } from 'utils/fileTypes'
import { getFileByUsage, getOriginalImageById } from 'utils/files'

import * as s from './LabeledGraphicFormValueItem.module.scss'
import type { LabeledGraphicEditorElement } from './labeledGraphicTypes'

const useShowIcon = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<LabeledGraphicElementValue>(name, 3)
  const hidden = value?.type !== 'image'

  return useMemo(() => ({ hidden }), [hidden])
}

export const useDefaultIcon = ({ name }: { name: string }) => {
  const device = useProjectContext(getDeviceMode)
  const { style } = useFormData<LabeledGraphicEditorElement>(name, 4) || {}
  const elementStyles = useElementStyleValues('labeledGraphic', device, style)
  const defaultValue = useMemo(() => {
    if (!elementStyles?.point?.backgroundImage) {
      return null
    }
    return {
      id: elementStyles?.point?.backgroundImage,
      type: FileUsageTypeEnum.image,
      source: FileUsageSource.file,
    } satisfies FileUsage
  }, [elementStyles?.point?.backgroundImage])

  return useMemo(() => ({ defaultValue }), [defaultValue])
}
export const POINT_IMG = genElementValueField({
  as: FieldFile<FileUsage | null>,
  name: 'pointIcon',
  layout: 'vertical',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    showSource: true,
    preview: true,
  },
  useConfigs: [useShowIcon, useDefaultIcon],
})

const IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'image',
  layout: 'vertical',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasAccessibility: true,
  },
})

const OVERLAY = genElementValueField({
  name: 'overlay',
  as: FieldColorPicker<string | null>,
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions],
})
interface IProps {
  name: string
  index: number
  item: LabeledGraphicPoint
  setForm: React.Dispatch<React.SetStateAction<'root' | 'icon'>>
}
const RootHeader: React.FC<IProps> = ({ index }) => (
  <Typography weight='medium'>
    {t('input.label.point')} {index + 1}
  </Typography>
)

const RootPanel: React.FC<IProps> = ({ name, index, setForm, item }) => {
  const file = getFileByUsage(item.image, FileUsageTypeEnum.image)
  const icon = getFileByUsage(item.pointIcon, FileUsageTypeEnum.image)
  const { style, value } = useFormData<LabeledGraphicEditorElement>(name, 2) || {}
  const device = useProjectContext(getDeviceMode)
  const brandIcon = useDefaultIcon({ name: `${name}.${index}. ${POINT_IMG.name}` })
  const elementStyles = useElementStyleCss('labeledGraphic', device, style)
  const defaultIcon = getOriginalImageById(brandIcon.defaultValue?.id)
  const iconStyle = useMemo(
    () => ({ color: '#ffffff', backgroundColor: elementStyles?.point?.backgroundColor }),
    [elementStyles?.point?.backgroundColor],
  )
  const isIcon = value?.type === 'image'

  return (
    <Padding gap>
      {isIcon && (
        <TileButton
          icon='otherAdd'
          name='pointImg'
          onClick={() => setForm('icon')}
          styleType='stroke'
        >
          <PanelIconImage
            defaultPath={defaultIcon?.path}
            file={item.pointIcon}
            icon='otherAdd'
            iconSize={KitSize.S}
            iconStyle={iconStyle}
          />
          <Typography rows={1} styleType='hint' tooltip={file?.name}>
            {icon?.name || t('input.label.icon')}
          </Typography>
        </TileButton>
      )}
      <Typography weight='medium'>{t('input.label.image')}</Typography>
      <FormField config={chainName(name, `${index}`, IMAGE)} />
      <FormField config={chainName(name, `${index}`, OVERLAY)} />
    </Padding>
  )
}

const IconHeader: React.FC<IProps> = ({ index, setForm }) => {
  const handleBack = () => setForm('root')
  return (
    <span className={s.floatingHeader}>
      <IconButton
        icon='back20Px'
        iconSize={KitSize.S}
        name='mainMenuBackButton'
        onClick={handleBack}
        size={KitSize.XS}
        styleType='ghost'
      />
      <Typography color='var(--color-placeholder)' onClick={handleBack} weight='medium'>
        {t('input.label.point')} {index + 1}
      </Typography>
      <Divider styleType='slash' free />
      <Typography> {t('input.label.icon')}</Typography>
    </span>
  )
}

const IconPanel: React.FC<IProps> = ({ name, index }) => {
  return (
    <Padding gap>
      <FormField config={chainName(name, `${index}`, POINT_IMG)} />
    </Padding>
  )
}

export default {
  root: { header: RootHeader, panel: RootPanel },
  icon: { header: IconHeader, panel: IconPanel },
}
